// hooks/useCommentSystem.js
import { useState, useEffect, useCallback, useRef } from "react";
import { commentService } from "../components/services/commentService";

export function useCommentSystem(containerRef, pageNumber, zoom, commentMode, externalComments, onCommentsChange, documentTitle, documentKey) {
  const [localComments, setLocalComments] = useState([]);
  const [activeComment, setActiveComment] = useState(null);
  const [tempComment, setTempComment] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasInitialLoad, setHasInitialLoad] = useState(false);
  
  // Refs to track state and prevent duplicates
  const lastSavedHash = useRef('');
  const isSaving = useRef(false);
  const lastSaveTimeout = useRef(null);

  // Generate a unique hash for comments to detect changes
  const generateCommentsHash = (commentsArray) => {
    return commentsArray
      .map(c => `${c.id}-${c.normalizedTop}-${c.normalizedLeft}-${c.text}`)
      .sort()
      .join('|');
  };

  // RESET when documentKey changes
  useEffect(() => {
    console.log('🔄 Document key changed, resetting comment system:', documentKey);
    setHasInitialLoad(false);
    lastSavedHash.current = '';
    isSaving.current = false;
    setLocalComments([]);
    setActiveComment(null);
    setTempComment(null);
    setIsLoading(false);
    
    // Clear any pending save timeout
    if (lastSaveTimeout.current) {
      clearTimeout(lastSaveTimeout.current);
      lastSaveTimeout.current = null;
    }
  }, [documentKey]);

  // Load comments when document changes - ONLY ONCE per document
  useEffect(() => {
    if (!documentTitle || hasInitialLoad) {
      console.log('ℹ️ Skipping comment load - no title or already loaded');
      return;
    }

    const loadComments = async () => {
      setIsLoading(true);
      try {
        console.log('🔄 Loading comments for:', documentTitle);
        const savedComments = await commentService.getComments(documentTitle);
        console.log('📥 Raw comments from backend:', savedComments);
        
        if (savedComments && savedComments.length > 0) {
          const transformedComments = savedComments.map(c => {
            console.log('🔍 Processing comment from backend:', c);
            
            const comment = {
              id: c.id,
              pageNumber: c.pageNumber || 1,
              normalizedLeft: c.normalizedLeft || 0,
              normalizedTop: c.normalizedTop || 0,
              text: c.text || '',
              color: c.color || '#2196f3',
              createdAt: c.createdAt || new Date().toISOString()
            };
            
            console.log('✅ Transformed comment:', comment);
            return comment;
          });
          
          console.log('🎯 Setting transformed comments to state:', transformedComments.length);
          
          // Set the hash for the loaded comments to prevent immediate save
          lastSavedHash.current = generateCommentsHash(transformedComments);
          setLocalComments(transformedComments);
          if (onCommentsChange) {
            onCommentsChange(transformedComments);
          }
          setHasInitialLoad(true);
        } else {
          console.log('ℹ️ No comments found in backend');
          lastSavedHash.current = '';
          setHasInitialLoad(true);
        }
      } catch (error) {
        console.error('❌ Failed to load comments:', error);
        setHasInitialLoad(true);
      } finally {
        setIsLoading(false);
      }
    };

    loadComments();
  }, [documentTitle, onCommentsChange, hasInitialLoad]);

  // Save comments when they change - OPTIMIZED with better debouncing
  useEffect(() => {
    // Don't save on initial load or if no document title
    if (!hasInitialLoad || !documentTitle || isSaving.current) {
      console.log('ℹ️ Skipping comment save - not ready or already saving');
      return;
    }

    const currentHash = generateCommentsHash(localComments);
    
    // Don't save if comments haven't actually changed
    if (currentHash === lastSavedHash.current) {
      console.log('ℹ️ Skipping comment save - comments unchanged');
      return;
    }

    // Don't save empty arrays (unless we're intentionally clearing)
    if (localComments.length === 0 && lastSavedHash.current === '') {
      console.log('ℹ️ Skipping comment save - already empty');
      return;
    }

    console.log('🔄 Comments changed - preparing to save:', {
      commentsCount: localComments.length,
      hashChanged: currentHash !== lastSavedHash.current,
      document: documentTitle
    });

    // Clear any existing timeout
    if (lastSaveTimeout.current) {
      clearTimeout(lastSaveTimeout.current);
    }

    // Set new timeout with better debouncing
    lastSaveTimeout.current = setTimeout(async () => {
      // Prevent multiple simultaneous saves
      if (isSaving.current) {
        console.log('⏳ Already saving comments, skipping...');
        return;
      }

      isSaving.current = true;
      
      try {
        console.log('💫 Saving comments to backend...');
        
        // Remove any potential duplicates before saving
        const uniqueComments = localComments.filter((comment, index, array) => 
          array.findIndex(c => 
            c.id === comment.id && 
            c.normalizedTop === comment.normalizedTop &&
            c.normalizedLeft === comment.normalizedLeft
          ) === index
        );

        console.log('🔍 After duplicate removal:', {
          original: localComments.length,
          unique: uniqueComments.length
        });

        // Transform frontend format to backend format
        const commentsToSave = uniqueComments.map(comment => ({
          id: comment.id,
          pageNumber: comment.pageNumber || 1,
          normalizedLeft: comment.normalizedLeft || 0,
          normalizedTop: comment.normalizedTop || 0,
          text: comment.text || '',
          color: comment.color || '#2196f3',
          createdAt: comment.createdAt || new Date().toISOString()
        }));

        console.log('📤 Sending comments to backend:', commentsToSave.length);
        
        const success = await commentService.saveComments(documentTitle, commentsToSave);
        
        if (success) {
          console.log('✅ Comments saved successfully to backend');
          // Update the hash only after successful save
          lastSavedHash.current = generateCommentsHash(localComments);
        } else {
          console.log('❌ Failed to save comments to backend');
        }
      } catch (error) {
        console.error('❌ Error saving comments:', error);
      } finally {
        isSaving.current = false;
      }
    }, 2000); // Increased to 2 seconds for better debouncing

    return () => {
      if (lastSaveTimeout.current) {
        clearTimeout(lastSaveTimeout.current);
        lastSaveTimeout.current = null;
      }
    };
  }, [localComments, documentTitle, hasInitialLoad]);

  // Handle click on PDF to add comment
  const handlePdfClick = useCallback((event) => {
    if (!commentMode || !containerRef.current) return;

    // Don't create comment if clicking on existing comment or buttons
    if (event.target.closest('.comment-marker') || event.target.closest('.comment-box')) {
      return;
    }

    const container = containerRef.current;
    const textLayer = container.querySelector(
      `.react-pdf__Page[data-page-number="${pageNumber}"] .react-pdf__Page__textContent`
    );
    
    if (!textLayer) return;

    const layerRect = textLayer.getBoundingClientRect();
    
    // Calculate normalized coordinates (0-1 range)
    const clickX = event.clientX;
    const clickY = event.clientY;
    
    const normalizedLeft = (clickX - layerRect.left) / layerRect.width;
    const normalizedTop = (clickY - layerRect.top) / layerRect.height;

    // Validate coordinates are within bounds
    if (normalizedLeft < 0 || normalizedLeft > 1 || normalizedTop < 0 || normalizedTop > 1) {
      return;
    }

    // Create temporary comment position
    const newComment = {
      id: `temp_${Date.now()}`,
      pageNumber,
      normalizedLeft,
      normalizedTop,
      text: "",
      color: '#2196f3',
      createdAt: new Date().toISOString(),
      isEditing: true
    };

    console.log('🎯 Creating new comment:', newComment);
    setTempComment(newComment);
    setActiveComment(newComment.id);
  }, [commentMode, containerRef, pageNumber]);

  // Add event listener for PDF clicks
  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    
    if (commentMode) {
      container.addEventListener('click', handlePdfClick);
      container.style.cursor = 'crosshair';
    } else {
      container.removeEventListener('click', handlePdfClick);
      container.style.cursor = 'default';
      // Clear active comment when mode is turned off
      setActiveComment(null);
      setTempComment(null);
    }

    return () => {
      container.removeEventListener('click', handlePdfClick);
      container.style.cursor = 'default';
    };
  }, [commentMode, handlePdfClick, containerRef]);

  // Save comment
  const saveComment = useCallback((commentId, text) => {
    if (!tempComment) return;

    const savedComment = {
      ...tempComment,
      id: `comment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, // Generate new permanent ID
      text: text,
      isEditing: false
    };

    console.log('💾 Saving comment:', savedComment);

    setLocalComments(prev => {
      const newComments = [...prev, savedComment];
      if (onCommentsChange) {
        onCommentsChange(newComments);
      }
      return newComments;
    });

    setTempComment(null);
    setActiveComment(null);
  }, [tempComment, onCommentsChange]);

  // Delete comment
  const deleteComment = useCallback((commentId) => {
    console.log('🗑️ Deleting comment:', commentId);
    
    setLocalComments(prev => {
      const newComments = prev.filter(comment => comment.id !== commentId);
      if (onCommentsChange) {
        onCommentsChange(newComments);
      }
      return newComments;
    });
    
    if (activeComment === commentId) {
      setActiveComment(null);
    }
    if (tempComment?.id === commentId) {
      setTempComment(null);
    }
  }, [activeComment, tempComment, onCommentsChange]);

  // Update comment text
  const updateCommentText = useCallback((commentId, newText) => {
    console.log('📝 Updating comment text:', { commentId, newText });
    
    setLocalComments(prev => prev.map(comment => 
      comment.id === commentId ? { ...comment, text: newText } : comment
    ));
  }, []);

  // Cancel comment creation
  const cancelComment = useCallback(() => {
    console.log('❌ Canceling comment creation');
    setTempComment(null);
    setActiveComment(null);
  }, []);

  // Move comment to new position
  const moveComment = useCallback((commentId, newNormalizedLeft, newNormalizedTop) => {
    console.log('📦 Moving comment:', { commentId, newNormalizedLeft, newNormalizedTop });
    
    setLocalComments(prev => {
      const newComments = prev.map(comment => 
        comment.id === commentId 
          ? { ...comment, normalizedLeft: newNormalizedLeft, normalizedTop: newNormalizedTop }
          : comment
      );
      if (onCommentsChange) {
        onCommentsChange(newComments);
      }
      return newComments;
    });
  }, [onCommentsChange]);

  const clearComments = useCallback(() => {
    console.log('🗑️ Clearing all comments');
    lastSavedHash.current = ''; // Reset hash when clearing
    setLocalComments([]);
    setActiveComment(null);
    setTempComment(null);
    if (onCommentsChange) {
      onCommentsChange([]);
    }
  }, [onCommentsChange]);

  const clearCurrentPageComments = useCallback(() => {
    console.log('🗑️ Clearing current page comments');
    setLocalComments(prev => {
      const newComments = prev.filter(comment => comment.pageNumber !== pageNumber);
      if (onCommentsChange) {
        onCommentsChange(newComments);
      }
      return newComments;
    });
  }, [pageNumber, onCommentsChange]);

  // Refresh comments manually
  const refreshComments = useCallback(async () => {
    if (!documentTitle) {
      console.log('❌ No document title for refresh');
      return;
    }

    setIsLoading(true);
    try {
      console.log('🔄 Manually refreshing comments...');
      const savedComments = await commentService.getComments(documentTitle);
      console.log('📥 Refreshed comments:', savedComments?.length);
      
      if (savedComments && savedComments.length > 0) {
        const transformedComments = savedComments.map(c => ({
          id: c.id,
          pageNumber: c.pageNumber || 1,
          normalizedLeft: c.normalizedLeft || 0,
          normalizedTop: c.normalizedTop || 0,
          text: c.text || '',
          color: c.color || '#2196f3',
          createdAt: c.createdAt || new Date().toISOString()
        }));
        
        console.log('✅ Setting refreshed comments:', transformedComments.length);
        lastSavedHash.current = generateCommentsHash(transformedComments);
        setLocalComments(transformedComments);
        if (onCommentsChange) {
          onCommentsChange(transformedComments);
        }
      } else {
        console.log('ℹ️ No comments found during refresh');
        lastSavedHash.current = '';
        setLocalComments([]);
        if (onCommentsChange) {
          onCommentsChange([]);
        }
      }
    } catch (error) {
      console.error('❌ Failed to refresh comments:', error);
    } finally {
      setIsLoading(false);
    }
  }, [documentTitle, onCommentsChange]);

  return {
    comments: localComments,
    activeComment,
    tempComment,
    saveComment,
    deleteComment,
    updateCommentText,
    cancelComment,
    clearComments,
    clearCurrentPageComments,
    refreshComments,
    setActiveComment,
    moveComment,
    isLoading
  };
}